/**
 * Emotional Architecture Heatmap — 12 emotions x scenes timeline.
 *
 * Dual rendering:
 *   <= 50 scenes  -> upgraded DOM-based heatmap
 *   >  50 scenes  -> Canvas-rendered heatmap for performance
 */
import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/* ─── Design tokens ─── */
const EMOTION_COLORS = {
  joy: '#FFD700',
  love: '#FF4D6A',
  humor: '#FFB347',
  wonder: '#7B68EE',
  tension: '#FF6B35',
  fear: '#9B2335',
  sadness: '#4A6FA5',
  anger: '#DC143C',
  disgust: '#556B2F',
  confusion: '#808080',
  nostalgia: '#DEB887',
  catharsis: '#E6E6FA',
};

const EMOTION_KEYS = Object.keys(EMOTION_COLORS);

const EMOTION_LABELS = {
  joy: 'Joy', love: 'Love', humor: 'Humor', wonder: 'Wonder',
  tension: 'Tension', fear: 'Fear', sadness: 'Sadness', anger: 'Anger',
  disgust: 'Disgust', confusion: 'Confusion', nostalgia: 'Nostalgia', catharsis: 'Catharsis',
};

const BG_COLOR = '#09090B';
const DIVIDER_COLOR = '#222228';
const GOLD = '#C9A84C';
const DEAD_ZONE_OVERLAY = 'rgba(128,128,128,0.4)';
const CANVAS_THRESHOLD = 50;

/* ─── Helpers ─── */
function hexToRgb(hex) {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function intensityToRgba(hex, intensity) {
  const [r, g, b] = hexToRgb(hex);
  const alpha = Math.max(0.05, intensity / 100);
  return `rgba(${r},${g},${b},${alpha})`;
}

function computeComposite(scene) {
  return scene.intensity_composite ?? 0;
}

/* ─── Sparkline (SVG, tiny) ─── */
function Sparkline({ values, width = 200, height = 28 }) {
  if (!values || values.length < 2) return null;
  const max = Math.max(...values, 1);
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - (v / max) * (height - 2) - 1;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={width} height={height} className="shrink-0">
      <polyline
        points={points}
        fill="none"
        stroke="#C9A84C"
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* ─── Summary Header ─── */
function SummaryHeader({ data, scenes }) {
  const deadZoneCount = data.dead_zones?.length ?? 0;
  const composites = scenes.map(computeComposite);
  const peakIdx = composites.indexOf(Math.max(...composites));
  const peakPage = scenes[peakIdx]?.page_number ?? scenes[peakIdx]?.scene_number ?? peakIdx + 1;

  return (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <div className="min-w-0">
        <h3
          className="text-sm font-semibold text-[var(--text-primary)]"
          style={{ fontFamily: 'DM Sans, sans-serif' }}
        >
          Emotional Architecture
        </h3>
        <p
          className="text-[11px] text-[var(--text-muted)] mt-0.5"
          style={{ fontFamily: 'DM Sans, sans-serif' }}
        >
          {deadZoneCount} dead zone{deadZoneCount !== 1 ? 's' : ''} detected
          {' \u00B7 '}Peak intensity at page {peakPage}
        </p>
      </div>
      <Sparkline values={composites} />
      <div className="flex gap-3 text-[10px] text-[var(--text-muted)]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
        <span>Pacing: <span className="text-amber-400 font-medium">{data.flow_metrics?.pacing_score || '\u2014'}</span></span>
        <span>Peaks: <span className="text-amber-400 font-medium">{data.flow_metrics?.peak_count || 0}</span></span>
        <span>Dead Zones: <span className="text-red-400 font-medium">{deadZoneCount}</span></span>
      </div>
    </div>
  );
}

/* ─── Left Legend (vertical, matches Y-axis) ─── */
function VerticalLegend({ cellH }) {
  return (
    <div className="flex flex-col shrink-0" style={{ gap: 0 }}>
      {EMOTION_KEYS.map((emo) => (
        <div
          key={emo}
          className="flex items-center gap-1.5 pr-2"
          style={{
            height: cellH,
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 11,
            color: 'var(--text-muted)',
            whiteSpace: 'nowrap',
          }}
        >
          <span
            className="w-2.5 h-2.5 rounded-sm shrink-0"
            style={{ backgroundColor: EMOTION_COLORS[emo] }}
          />
          {EMOTION_LABELS[emo]}
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   DOM Heatmap  (scenes <= 50)
   ══════════════════════════════════════════════════════════════════ */
function DOMHeatmap({ scenes, data, selectedScene, setSelectedScene }) {
  const cellH = 22;

  const deadZoneSet = useMemo(() => {
    const s = new Set();
    (data.dead_zones || []).forEach((dz) => {
      const range = dz.scene_range;
      if (typeof range === 'string') {
        const [a, b] = range.split('-').map(Number);
        for (let n = a; n <= b; n++) s.add(n);
      }
    });
    scenes.forEach((sc, i) => { if (sc.is_dead_zone) s.add(sc.scene_number ?? i + 1); });
    return s;
  }, [scenes, data.dead_zones]);

  return (
    <div className="flex gap-0">
      <VerticalLegend cellH={cellH} />
      <div className="flex-1 overflow-x-auto">
        {/* Scene number header */}
        <div className="flex" style={{ gap: 1, marginBottom: 2 }}>
          {scenes.map((sc, i) => {
            const num = sc.scene_number ?? i + 1;
            const show = scenes.length <= 20 || num % 5 === 0 || i === 0;
            return (
              <div
                key={i}
                className="flex-1 min-w-[6px] text-center"
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: 10,
                  color: 'var(--text-muted)',
                }}
              >
                {show ? num : ''}
              </div>
            );
          })}
        </div>

        {/* Grid rows — one per emotion */}
        {EMOTION_KEYS.map((emo) => (
          <div key={emo} className="flex" style={{ gap: 1, height: cellH }}>
            {scenes.map((sc, i) => {
              const intensity = sc.emotions?.[emo] ?? 0;
              const isDeadZone = deadZoneSet.has(sc.scene_number ?? i + 1) || sc.is_dead_zone;
              const isSel = selectedScene === i;
              const isActDiv = [0.25, 0.5, 0.75].some(
                (f) => Math.abs(i - Math.round(scenes.length * f)) < 1
              );
              return (
                <TooltipProvider delayDuration={80} key={i}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => setSelectedScene(isSel ? null : i)}
                        className="flex-1 min-w-[6px] transition-all"
                        style={{
                          backgroundColor: intensity > 0 ? intensityToRgba(EMOTION_COLORS[emo], intensity) : 'transparent',
                          borderLeft: isActDiv ? `2px solid ${GOLD}` : `1px solid ${DIVIDER_COLOR}`,
                          outline: isSel ? `1.5px solid ${GOLD}` : 'none',
                          position: 'relative',
                        }}
                      >
                        {isDeadZone && (
                          <span
                            className="absolute inset-0"
                            style={{ backgroundColor: DEAD_ZONE_OVERLAY }}
                          />
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="bg-[var(--bg-elevated)] border-[var(--border-subtle)] text-xs max-w-[220px]">
                      <p className="font-medium text-[var(--text-primary)]">
                        Scene {sc.scene_number ?? i + 1}
                      </p>
                      <p className="text-[var(--text-muted)]">
                        {EMOTION_LABELS[emo]}: {intensity}
                      </p>
                      <p className="text-amber-400 mt-0.5">
                        Primary: {sc.primary_emotion} &middot; Engagement: {sc.engagement_prediction ?? '\u2014'}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })}
          </div>
        ))}

        {/* Engagement overlay line (DOM version — SVG) */}
        <svg
          width="100%"
          height="4"
          viewBox={`0 0 ${scenes.length} 1`}
          preserveAspectRatio="none"
          className="mt-0.5"
        >
          <polyline
            points={scenes.map((sc, i) => `${i + 0.5},${1 - (computeComposite(sc) / 100)}`).join(' ')}
            fill="none"
            stroke="white"
            strokeWidth="0.15"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Canvas Heatmap  (scenes > 50)
   ══════════════════════════════════════════════════════════════════ */
function CanvasHeatmap({ scenes, data, selectedScene, setSelectedScene }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const miniRef = useRef(null);
  const [hover, setHover] = useState(null); // { sceneIdx, emotionIdx, x, y }
  const [scrollLeft, setScrollLeft] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);

  const CELL_W = 10;
  const CELL_H = 22;
  const HEADER_H = 18;
  const ENGAGEMENT_H = 24;
  const EMOTIONS = EMOTION_KEYS;
  const totalCols = scenes.length;
  const canvasLogicalW = totalCols * CELL_W;
  const canvasLogicalH = HEADER_H + EMOTIONS.length * CELL_H + ENGAGEMENT_H;

  // Dead zone scene numbers
  const deadZoneSet = useMemo(() => {
    const s = new Set();
    (data.dead_zones || []).forEach((dz) => {
      const range = dz.scene_range;
      if (typeof range === 'string') {
        const [a, b] = range.split('-').map(Number);
        for (let n = a; n <= b; n++) s.add(n);
      }
    });
    scenes.forEach((sc, i) => { if (sc.is_dead_zone) s.add(sc.scene_number ?? i + 1); });
    return s;
  }, [scenes, data.dead_zones]);

  // Pre-computed RGB values
  const emotionRgb = useMemo(() => {
    const m = {};
    for (const emo of EMOTIONS) m[emo] = hexToRgb(EMOTION_COLORS[emo]);
    return m;
  }, []);

  // Composites for engagement line
  const composites = useMemo(() => scenes.map(computeComposite), [scenes]);
  const maxComposite = useMemo(() => Math.max(...composites, 1), [composites]);

  /* ─── Draw ─── */
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvasLogicalW * dpr;
    canvas.height = canvasLogicalH * dpr;
    canvas.style.width = `${canvasLogicalW}px`;
    canvas.style.height = `${canvasLogicalH}px`;
    ctx.scale(dpr, dpr);

    // Background
    ctx.fillStyle = BG_COLOR;
    ctx.fillRect(0, 0, canvasLogicalW, canvasLogicalH);

    // Scene number interval
    const labelEvery = totalCols > 200 ? 20 : totalCols > 100 ? 10 : 5;

    // Header — scene numbers
    ctx.font = '10px "JetBrains Mono", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#666';
    for (let i = 0; i < totalCols; i++) {
      const num = scenes[i].scene_number ?? i + 1;
      if (num % labelEvery === 0 || i === 0) {
        ctx.fillText(String(num), i * CELL_W + CELL_W / 2, HEADER_H / 2);
      }
    }

    // Heatmap cells
    for (let row = 0; row < EMOTIONS.length; row++) {
      const emo = EMOTIONS[row];
      const [r, g, b] = emotionRgb[emo];
      const y = HEADER_H + row * CELL_H;
      for (let col = 0; col < totalCols; col++) {
        const sc = scenes[col];
        const intensity = sc.emotions?.[emo] ?? 0;
        const x = col * CELL_W;
        const alpha = Math.max(0.05, intensity / 100);
        ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
        ctx.fillRect(x, y, CELL_W, CELL_H);

        // Scene divider
        ctx.fillStyle = DIVIDER_COLOR;
        ctx.fillRect(x + CELL_W - 1, y, 1, CELL_H);
      }
    }

    // Act dividers (25%, 50%, 75%)
    ctx.strokeStyle = GOLD;
    ctx.lineWidth = 2;
    for (const frac of [0.25, 0.5, 0.75]) {
      const x = Math.round(totalCols * frac) * CELL_W;
      ctx.beginPath();
      ctx.moveTo(x, HEADER_H);
      ctx.lineTo(x, HEADER_H + EMOTIONS.length * CELL_H);
      ctx.stroke();
    }

    // Dead zone overlays
    for (let col = 0; col < totalCols; col++) {
      const sc = scenes[col];
      const num = sc.scene_number ?? col + 1;
      if (deadZoneSet.has(num) || sc.is_dead_zone) {
        ctx.fillStyle = DEAD_ZONE_OVERLAY;
        ctx.fillRect(col * CELL_W, HEADER_H, CELL_W, EMOTIONS.length * CELL_H);
      }
    }

    // Selected column highlight
    if (selectedScene !== null && selectedScene >= 0 && selectedScene < totalCols) {
      ctx.strokeStyle = GOLD;
      ctx.lineWidth = 2;
      ctx.strokeRect(
        selectedScene * CELL_W + 1,
        HEADER_H,
        CELL_W - 2,
        EMOTIONS.length * CELL_H
      );
    }

    // Hover highlight
    if (hover) {
      ctx.strokeStyle = 'rgba(255,255,255,0.5)';
      ctx.lineWidth = 1;
      ctx.strokeRect(
        hover.sceneIdx * CELL_W,
        HEADER_H + hover.emotionIdx * CELL_H,
        CELL_W,
        CELL_H
      );
    }

    // Engagement line
    const engY0 = HEADER_H + EMOTIONS.length * CELL_H;
    ctx.fillStyle = 'rgba(9,9,11,0.8)';
    ctx.fillRect(0, engY0, canvasLogicalW, ENGAGEMENT_H);
    ctx.beginPath();
    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.lineWidth = 2;
    for (let i = 0; i < totalCols; i++) {
      const x = i * CELL_W + CELL_W / 2;
      const y = engY0 + ENGAGEMENT_H - (composites[i] / maxComposite) * (ENGAGEMENT_H - 4) - 2;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }, [scenes, selectedScene, hover, deadZoneSet, emotionRgb, composites, maxComposite, totalCols, canvasLogicalW, canvasLogicalH]);

  /* ─── Draw mini-overview ─── */
  const drawMini = useCallback(() => {
    const canvas = miniRef.current;
    if (!canvas || !containerWidth || canvasLogicalW <= containerWidth) return;
    const dpr = window.devicePixelRatio || 1;
    const miniW = containerWidth;
    const miniH = 20;
    canvas.width = miniW * dpr;
    canvas.height = miniH * dpr;
    canvas.style.width = `${miniW}px`;
    canvas.style.height = `${miniH}px`;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    ctx.fillStyle = BG_COLOR;
    ctx.fillRect(0, 0, miniW, miniH);

    const scaleX = miniW / totalCols;
    // Compressed heatmap
    for (let col = 0; col < totalCols; col++) {
      const sc = scenes[col];
      const composite = computeComposite(sc);
      const primary = sc.primary_emotion;
      const color = EMOTION_COLORS[primary] || '#666';
      const [r, g, b] = hexToRgb(color);
      ctx.fillStyle = `rgba(${r},${g},${b},${Math.max(0.1, composite / 100)})`;
      ctx.fillRect(col * scaleX, 0, Math.max(scaleX, 1), miniH);
    }

    // Viewport indicator
    const vpLeft = (scrollLeft / canvasLogicalW) * miniW;
    const vpWidth = (containerWidth / canvasLogicalW) * miniW;
    ctx.strokeStyle = GOLD;
    ctx.lineWidth = 1.5;
    ctx.strokeRect(vpLeft, 0, vpWidth, miniH);
  }, [containerWidth, scrollLeft, scenes, totalCols, canvasLogicalW]);

  /* ─── Effects ─── */
  useEffect(() => { draw(); }, [draw]);
  useEffect(() => { drawMini(); }, [drawMini]);

  // Resize observer
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let timer;
    const ro = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        setContainerWidth(el.clientWidth);
      }, 100);
    });
    ro.observe(el);
    setContainerWidth(el.clientWidth);
    return () => { ro.disconnect(); clearTimeout(timer); };
  }, []);

  // Scroll sync
  const handleScroll = useCallback((e) => {
    setScrollLeft(e.currentTarget.scrollLeft);
  }, []);

  /* ─── Mouse interactions ─── */
  const hitTest = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const x = (e.clientX - rect.left);
    const y = (e.clientY - rect.top);
    const col = Math.floor(x / CELL_W);
    const row = Math.floor((y - HEADER_H) / CELL_H);
    if (col < 0 || col >= totalCols || row < 0 || row >= EMOTIONS.length) return null;
    return { sceneIdx: col, emotionIdx: row, x: e.clientX, y: e.clientY };
  }, [totalCols]);

  const onMouseMove = useCallback((e) => {
    const hit = hitTest(e);
    setHover(hit);
  }, [hitTest]);

  const onMouseLeave = useCallback(() => setHover(null), []);

  const onClick = useCallback((e) => {
    const hit = hitTest(e);
    if (hit) {
      setSelectedScene((prev) => prev === hit.sceneIdx ? null : hit.sceneIdx);
    }
  }, [hitTest, setSelectedScene]);

  const needsScroll = canvasLogicalW > containerWidth;

  return (
    <div className="flex gap-0">
      <VerticalLegend cellH={CELL_H} />
      <div className="flex-1 min-w-0 relative" ref={containerRef}>
        <div
          className="overflow-x-auto"
          onScroll={handleScroll}
          style={{ maxHeight: canvasLogicalH + 8 }}
        >
          <canvas
            ref={canvasRef}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
            onClick={onClick}
            style={{ cursor: 'crosshair', display: 'block' }}
          />
        </div>

        {/* Floating tooltip */}
        {hover && (() => {
          const sc = scenes[hover.sceneIdx];
          const emo = EMOTIONS[hover.emotionIdx];
          const intensity = sc.emotions?.[emo] ?? 0;
          return (
            <div
              className="fixed z-50 pointer-events-none rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-elevated)] px-3 py-2 text-xs shadow-xl"
              style={{
                left: hover.x + 12,
                top: hover.y - 60,
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              <p className="font-medium text-[var(--text-primary)]">
                Scene {sc.scene_number ?? hover.sceneIdx + 1}
              </p>
              <p className="text-[var(--text-muted)]">
                {EMOTION_LABELS[emo]}: <span style={{ color: EMOTION_COLORS[emo] }}>{intensity}</span>
              </p>
              <p className="text-amber-400 mt-0.5">
                Primary: {sc.primary_emotion} &middot; Engagement: {sc.engagement_prediction ?? '\u2014'}
              </p>
            </div>
          );
        })()}

        {/* Mini-overview scrollbar */}
        {needsScroll && (
          <canvas
            ref={miniRef}
            className="mt-1 rounded cursor-pointer"
            style={{ display: 'block' }}
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const frac = (e.clientX - rect.left) / rect.width;
              const target = frac * canvasLogicalW - containerWidth / 2;
              const scrollEl = containerRef.current?.querySelector('.overflow-x-auto');
              if (scrollEl) scrollEl.scrollLeft = Math.max(0, target);
            }}
          />
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Main Export
   ══════════════════════════════════════════════════════════════════ */
export default function EmotionalHeatmap({ data }) {
  const [selectedScene, setSelectedScene] = useState(null);

  if (!data?.scenes?.length) return null;

  const scenes = data.scenes;
  const useCanvas = scenes.length > CANVAS_THRESHOLD;

  return (
    <div className="space-y-4">
      {/* Summary Header */}
      <SummaryHeader data={data} scenes={scenes} />

      {/* Heatmap — dual mode */}
      <div
        className="rounded-lg p-3"
        style={{ backgroundColor: BG_COLOR }}
      >
        {useCanvas ? (
          <CanvasHeatmap
            scenes={scenes}
            data={data}
            selectedScene={selectedScene}
            setSelectedScene={setSelectedScene}
          />
        ) : (
          <DOMHeatmap
            scenes={scenes}
            data={data}
            selectedScene={selectedScene}
            setSelectedScene={setSelectedScene}
          />
        )}
      </div>

      {/* Selected Scene Detail */}
      {selectedScene !== null && scenes[selectedScene] && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-elevated)] p-4 space-y-2"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-[var(--text-primary)]">
              {scenes[selectedScene].slug_line || `Scene ${scenes[selectedScene].scene_number}`}
            </span>
            <button onClick={() => setSelectedScene(null)} className="text-[10px] text-[var(--text-muted)]">Close</button>
          </div>
          <p className="text-xs text-[var(--text-secondary)]">{scenes[selectedScene].emotional_summary}</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(scenes[selectedScene].emotions || {})
              .filter(([, v]) => v > 10)
              .sort((a, b) => b[1] - a[1])
              .map(([emotion, intensity]) => (
                <span
                  key={emotion}
                  className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                  style={{
                    backgroundColor: `${EMOTION_COLORS[emotion]}20`,
                    color: EMOTION_COLORS[emotion],
                  }}
                >
                  {EMOTION_LABELS[emotion]} {intensity}
                </span>
              ))}
          </div>
        </motion.div>
      )}

      {/* Dead Zones */}
      {data.dead_zones?.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-red-400">Dead Zones Detected</h4>
          {data.dead_zones.map((dz, i) => (
            <div key={i} className="rounded-lg border border-red-500/20 bg-red-500/5 p-3 text-xs">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-red-400 font-medium">{dz.type.replace(/_/g, ' ')}</span>
                <span className="text-[var(--text-muted)]">Scenes {dz.scene_range}</span>
              </div>
              <p className="text-[var(--text-secondary)]">{dz.description}</p>
              {dz.suggestions?.length > 0 && (
                <ul className="mt-1 space-y-0.5">
                  {dz.suggestions.map((s, j) => (
                    <li key={j} className="text-[var(--text-muted)] pl-2 border-l border-red-500/30">{s}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
